import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Select, message, Card } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../components/DataTable';
import { CollaboratorData, ResourceType, ResourceUnit, RESOURCE_UNIT_LABEL_KEYS } from '../models/Resource';
import {
  getAllCollaborators,
  createCollaborator,
  updateCollaborator,
  deleteCollaborator
} from '../services/supplierService';
import { getApiErrorMessage } from '../utils/errorMessages';
import { useTranslation } from 'react-i18next';
const { Option } = Select;

const Teams: React.FC = () => {
  const [data, setData] = useState<CollaboratorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<CollaboratorData | null>(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllCollaborators();
      const resourceArray = response.execution_resources || response;
      const formattedData = resourceArray
        .filter((item: any) => item.type === ResourceType.MAN_POWER)
        .map((item: any) => {
          const pricing = item.pricing_options?.[0];
          return {
            id: item.id,
            id_pricing: pricing?.id || '',
            name: item.name,
            job: item.metadata?.job || '',
            birth_date: item.metadata?.birth_date || '',
            unit: pricing?.unit || '',
            unit_price: pricing ? pricing.unit_price.toString() : '',
            description: item.description || ''
          };
        });
      setData(formattedData);
    } catch (error: any) {
      message.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<CollaboratorData> = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('jobTitle'),
      dataIndex: 'job',
      key: 'job',
    },
    {
      title: t('birthDate'),
      dataIndex: 'birth_date',
      key: 'birth_date',
    },
    {
      title: t('unit'),
      dataIndex: 'unit',
      key: 'unit',
      render: (unitValue: string) =>
        unitValue ? t(RESOURCE_UNIT_LABEL_KEYS[unitValue] || unitValue) : '-',
    },
    {
      title: t('unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price: string) => (price ? `${Number(price).toFixed(2)} €` : '-'),
    },
  ];

  const handleCreate = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: CollaboratorData) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCollaborator(id);
      message.success(t('deleteSuccess'));
      fetchData();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'deleteError', t));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingItem) {
        const updatePayload = {
          name: values.name,
          description: values.description,
          pricing: {
            pricing_id: editingItem.id_pricing,
            current_unit: editingItem.unit,
            unit: values.unit,
            unit_price: values.unit_price
          },
          metadata: {
            job: values.job,
            birth_date: values.birth_date
          }
        };
        await updateCollaborator(editingItem.id!, updatePayload);
        message.success(t('updateSuccess'));
      } else {
        await createCollaborator(values);
        message.success(t('createSuccess'));
      }
      setModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'saveError', t));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0, marginBottom: 16 }}>
        {t('teamsManagement')}
      </h1>
      <Card
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('add')}
            </Button>
          }
        >
          <DataTable
            data={data}
            columns={columns}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            searchPlaceholder={t('search')}
          />
        </Card>

        <Modal
          title={editingItem ? t('editTeamMember') : t('addTeamMember')}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={700}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="name"
              label={t('name')}
              rules={[{ required: true, message: t('nameRequired') }]}
            >
              <Input placeholder={t('name')} />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="job"
                label={t('jobTitle')}
                rules={[{ required: true, message: t('jobTitleRequired') }]}
              >
                <Input placeholder={t('jobTitle')} />
              </Form.Item>

              <Form.Item
                name="birth_date"
                label={t('birthDate')}
                rules={[
                  { required: true, message: t('birthDateRequired') },
                  { pattern: /^\d{2}-\d{2}-\d{4}$/, message: t('dateFormat') }
                ]}
              >
                <Input placeholder="DD-MM-YYYY" />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="unit"
                label={t('unit')}
                rules={[{ required: true, message: t('unitRequired') }]}
              >
                <Select placeholder={t('selectUnit')}>
                  {Object.values(ResourceUnit).map((value) => (
                    <Option key={value} value={value}>
                      {t(RESOURCE_UNIT_LABEL_KEYS[value])}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="unit_price"
                label={t('unitPrice')}
                rules={[
                  { required: true, message: t('unitPriceRequired') },
                  { pattern: /^\d+([.,]\d{1,2})?$/, message: t('invalidFormat') }
                ]}
              >
                <Input placeholder={t('unitPrice')} />
              </Form.Item>
            </div>

            <Form.Item
              name="description"
              label={t('notes')}
            >
              <Input.TextArea rows={3} placeholder={t('additionalNotes')} />
            </Form.Item>
          </Form>
        </Modal>
    </div>
  );
};

export default Teams;
